import React, { useRef, useEffect, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import { justDate, niceString } from '@/utils/datetime';
import {
  canUseAsUnreadMarker,
  isMessageUnreadByMe,
} from '@/utils/ReceiptUtils';
import SameDateMessages from './SameDateMessages';
import UnreadMarker from './UnreadMarker';
import cssStyles from './MessageList.css';

type Props = {
  currentSession: Session;
  currentPeer: Contact;
  ownerGuid: string;
  receiptObj: any;
  messages: Message[];
  messagesForPeerJid: string;
  onMessageIntersection: () => void;
  hasFuture: boolean;
  showUnreadMarker: boolean;
};

const MessageRenderer = function MessageRenderer({
  currentSession,
  currentPeer,
  ownerGuid,
  receiptObj,
  messages,
  messagesForPeerJid,
  onMessageIntersection,
  showUnreadMarker,
}: Props) {
  const marker = useRef(undefined);
  const currentPeerJid = currentPeer?.jid;

  /**
   * Unread marker rendering process:
   *
   * For chat with unread messages, render marker render above the first unread message.
   * For chats with no unread msgs, ignore.
   *
   * But if a user sends a message, then we shouldn't render at all. i.e we set unread
   * marker as -1.
   *
   * i.e
   * -1 => Marker will not render anymore.
   *
   * undefined => look for an unread marker.
   */
  useEffect(() => {
    marker.current = undefined;
  }, [currentPeerJid]);

  const getRenderObject = useCallback(() => {
    let currentDateCollection;
    const renderObject = [];

    messages.forEach((msg, index, allMessages) => {
      const date = justDate(msg.sentTime);

      if (!showUnreadMarker) {
        marker.current = { sid: -1 };
      }

      if (!marker.current) {
        const firstMessage = allMessages[0];
        const isFirstMessageUnRead = firstMessage
          ? isMessageUnreadByMe(receiptObj, firstMessage)
          : false;
        const prevMsg = allMessages[index - 1];
        const isPrevMsgUsable = prevMsg
          ? canUseAsUnreadMarker(receiptObj, prevMsg)
          : false;
        const isCurrentMsgUsable = canUseAsUnreadMarker(receiptObj, msg);

        // First message of the list must be read for unread marker for the logic to work.
        if (
          !isFirstMessageUnRead &&
          prevMsg &&
          !isPrevMsgUsable &&
          isCurrentMsgUsable
        ) {
          marker.current = msg;
        }
      }

      if (marker.current?.sid === msg.sid) {
        currentDateCollection = {
          date,
          msgs: [],
          firstUnreadGroup: true,
          isUnreadGroup: true,
        };
        renderObject.push(currentDateCollection);
      }

      if (
        !currentDateCollection ||
        !isEqual(date, currentDateCollection.date)
      ) {
        currentDateCollection = {
          date,
          msgs: [],
          isUnreadGroup: marker.current
            ? marker.current.timestamp < msg.timestamp
            : false,
        };
        renderObject.push(currentDateCollection);
      }
      currentDateCollection.msgs.push(msg);
    });
    return renderObject;
  }, [messages, receiptObj, showUnreadMarker]);

  if (messagesForPeerJid !== currentPeer.jid) {
    return null;
  }
  return getRenderObject().map((dateGroup, index, allItems) => {
    const isLastDateGroup = index === allItems.length - 1;
    return (
      // Make sure the key here is same for subsequent renders, so that messages under the group
      // are not re-created.
      <React.Fragment key={`${dateGroup.date} ${dateGroup.isUnreadGroup}`}>
        {dateGroup.firstUnreadGroup ? <UnreadMarker /> : null}
        <div className={cssStyles.dateGroup}>
          <div className={cssStyles.dateWrap}>
            <div className={cssStyles.date}>{niceString(dateGroup.date)}</div>
          </div>
          <SameDateMessages
            msgs={dateGroup.msgs}
            currentSession={currentSession}
            currentPeer={currentPeer}
            ownerGuid={ownerGuid}
            receiptObj={receiptObj}
            isLastDateGroup={isLastDateGroup}
            onMessageIntersection={onMessageIntersection}
          />
        </div>
      </React.Fragment>
    );
  });
};

export default MessageRenderer;
